import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAppState } from "../context/AppStateContext";
import { downloadCsv } from "../utils/csvDownload";
import { downloadPdfFromHtml } from "../utils/downloadPdfFromHtml";
import { fetchLeadsTable, type LeadTableRow } from "../api/leadsTableApi";
import { 
  fetchIssuePoRegister, type IssuePoHeader,
  fetchIssueMaterialRegister, type IssueMaterialRow,
  fetchStoreInwardRegister, type StoreInwardReceipt,
  fetchStoreOutwardRegister, type StoreOutwardRow 
} from "../api/mrrInventoryApi";
import { 
  IconReport, IconFilter, IconDownload, IconLayers, IconBriefcase, 
  IconUserPlus, IconFolder, IconMilestone, IconResources, IconReceipt, 
  IconInward, IconOutward, IconClipboard, IconPdf
} from "../components/Icons";
import { SearchableSelect } from "../components/SearchableSelect";
import { GeoLocationFilters } from "../components/GeoLocationFilters";
import { useGeoDirectory } from "../context/GeoDirectoryContext";
import { geoLocationPassesFilter } from "../utils/geoDirectorySelectOptions";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";

type ModuleId = "leads" | "applicants" | "subcontractors" | "engineers" | "progress" | "po" | "material_issue" | "inwards" | "outwards" | "inventory";

const MODULES = [
  { id: "leads", title: "Leads Intelligence", icon: <IconLayers /> },
  { id: "applicants", title: "Applicants", icon: <IconFolder /> },
  { id: "subcontractors", title: "Subcontractor Performance", icon: <IconBriefcase /> },
  { id: "progress", title: "Work Progress", icon: <IconMilestone /> },
  { id: "engineers", title: "Engineering Field Staff", icon: <IconUserPlus /> },
  { id: "po", title: "Purchase Orders (PO)", icon: <IconReceipt /> },
  { id: "material_issue", title: "Material Issue", icon: <IconClipboard /> },
  { id: "inwards", title: "Inwards (Receipts)", icon: <IconInward /> },
  { id: "outwards", title: "Outwards (Dispatch)", icon: <IconOutward /> },
  { id: "inventory", title: "Inventory Master", icon: <IconResources /> }
] as const;

// --- MODERN BI WIDGET STYLING ---
const modernShadow = "0 4px 6px -1px rgba(0,0,0,0.05), 0 2px 4px -1px rgba(0,0,0,0.03)";

const AnimatedNumber = ({ value }: { value: number }) => {
  const [displayValue, setDisplayValue] = useState(0);

  useEffect(() => {
    let start = 0;
    const duration = 1000;
    const startTime = performance.now();
    let rAF: number;
    
    const animate = (currentTime: number) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      const easeProgress = progress === 1 ? 1 : 1 - Math.pow(2, -10 * progress);
      
      setDisplayValue(Math.floor(easeProgress * value));
      
      if (progress < 1) {
        rAF = requestAnimationFrame(animate);
      } else {
        setDisplayValue(value);
      }
    };
    
    rAF = requestAnimationFrame(animate);
    return () => cancelAnimationFrame(rAF);
  }, [value]);

  return <>{displayValue.toLocaleString()}</>;
};

const KpiWidget = ({
  title,
  value,
  subtitle,
  badge,
  color = "#3b82f6",
  variant = "metric",
}: {
  title: string;
  value: number;
  subtitle?: string;
  badge?: string;
  color?: string;
  variant?: "hero" | "metric";
}) => {
  const pctNum = badge?.endsWith("%") ? Number.parseInt(badge, 10) : null;
  const style = {
    "--report-kpi-accent": color,
  } as React.CSSProperties;

  return (
    <article
      className={`report-kpi-card${variant === "hero" ? " report-kpi-card--hero" : ""}`}
      style={style}
    >
      <div className="report-kpi-card__accent" aria-hidden />
      <div className="report-kpi-card__body">
        <div className="report-kpi-card__head">
          <span className="report-kpi-card__label" title={title}>
            {title}
          </span>
          {badge ? (
            <span className="report-kpi-card__badge">{badge}</span>
          ) : variant === "hero" ? (
            <span className="report-kpi-card__badge report-kpi-card__badge--neutral">Overview</span>
          ) : null}
        </div>
        <div className="report-kpi-card__value" aria-live="polite">
          <AnimatedNumber value={value} />
        </div>
        {pctNum != null && variant !== "hero" ? (
          <div
            className="report-kpi-card__track"
            role="img"
            aria-label={`${pctNum}% of total`}
          >
            <div
              className="report-kpi-card__fill"
              style={{ width: `${Math.min(100, Math.max(0, pctNum))}%` }}
            />
          </div>
        ) : null}
        {subtitle ? <p className="report-kpi-card__subtitle">{subtitle}</p> : null}
      </div>
    </article>
  );
};

const PieChart = ({ data, title }: { data: { label: string, value: number, color: string }[], title: string }) => {
  const total = Math.max(data.reduce((a, b) => a + b.value, 0), 1);
  let currentPercent = 0;
  const gradientStops = data.map(d => {
    const start = currentPercent;
    currentPercent += (d.value / total) * 100;
    return `${d.color} ${start}% ${currentPercent}%`;
  }).join(", ");

  return (
    <div style={{ background: "#fff", borderRadius: "8px", padding: "1.25rem", display: "flex", flexDirection: "column", height: "100%", boxShadow: modernShadow, border: "1px solid #f1f5f9" }}>
      <style>{`.hide-scroll::-webkit-scrollbar { display: none; } .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
      <h4 style={{ margin: "0 0 1rem", fontSize: "0.85rem", fontWeight: 600, color: "#334155" }}>{title}</h4>
      <div style={{ display: "flex", alignItems: "center", gap: "1.5rem", flex: 1 }}>
        <div style={{ width: "110px", height: "110px", borderRadius: "50%", background: `conic-gradient(${gradientStops})`, flexShrink: 0, boxShadow: "0 4px 6px -1px rgba(0,0,0,0.1)" }} />
        <div className="hide-scroll" style={{ display: "flex", flexDirection: "column", gap: "0.5rem", flex: 1, overflowY: "auto", maxHeight: "110px", paddingRight: "4px" }}>
          {data.map((d, i) => (
            <div key={i} style={{ display: "flex", alignItems: "center", justifyContent: "space-between", fontSize: "0.8rem" }}>
              <div style={{ display: "flex", alignItems: "center", gap: "0.5rem", overflow: "hidden" }}>
                <span style={{ width: "10px", height: "10px", borderRadius: "50%", background: d.color, display: "inline-block", flexShrink: 0 }} />
                <span style={{ color: "#475569", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "200px" }} title={d.label}>{d.label}</span>
              </div>
              <span style={{ fontWeight: 600, color: "#1e293b", marginLeft: "0.5rem" }}>{d.value}</span>
            </div>
          ))}
          {data.length === 0 && <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>No data points</div>}
        </div>
      </div>
    </div>
  );
};

const HorizontalBarChart = ({ data, title }: { data: { label: string, value: number }[], title: string }) => {
  const max = Math.max(...data.map(d => d.value), 1);
  return (
    <div style={{ background: "#fff", borderRadius: "8px", padding: "1.25rem", boxShadow: modernShadow, border: "1px solid #f1f5f9" }}>
      <h4 style={{ margin: "0 0 1rem", fontSize: "0.85rem", fontWeight: 600, color: "#334155" }}>{title}</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: "0.85rem" }}>
        {data.length === 0 && <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>No data available</div>}
        {data.map((d, i) => (
          <div key={i}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: "0.8rem", marginBottom: "0.25rem", color: "#475569" }}>
              <span style={{ whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", maxWidth: "85%" }} title={d.label}>{d.label}</span>
              <span style={{ fontWeight: 600, color: "#1e293b" }}>{d.value.toLocaleString()}</span>
            </div>
            <div style={{ height: "10px", background: "#f1f5f9", borderRadius: "5px", overflow: "hidden" }}>
              <div style={{ height: "100%", width: `${(d.value / max) * 100}%`, background: "#3b82f6", borderRadius: "5px" }} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const FunnelChart = ({ data, title }: { data: { label: string, value: number, color: string }[], title: string }) => {
  const max = Math.max(data[0]?.value || 1, 1);
  return (
    <div style={{ background: "#fff", borderRadius: "8px", padding: "1.25rem", boxShadow: modernShadow, border: "1px solid #f1f5f9", display: "flex", flexDirection: "column", height: "100%" }}>
      <h4 style={{ margin: "0 0 1rem", fontSize: "0.85rem", fontWeight: 600, color: "#334155" }}>{title}</h4>
      <div style={{ display: "flex", flexDirection: "column", gap: "1rem", flex: 1, justifyContent: "center" }}>
        {data.length === 0 && <div style={{ fontSize: "0.8rem", color: "#94a3b8" }}>No data available</div>}
        {data.map((d, i) => {
          const widthPct = (d.value / max) * 100;
          return (
            <div key={i} style={{ width: "100%", display: "flex", flexDirection: "column" }}>
              <div style={{ display: "flex", justifyContent: "space-between", width: "100%", fontSize: "0.8rem", marginBottom: "0.35rem", color: "#475569" }}>
                <span style={{ fontWeight: 500 }}>{d.label}</span>
                <span style={{ fontWeight: 600, color: "#1e293b" }}>{d.value.toLocaleString()} <span style={{ color: "#94a3b8", marginLeft: "0.25rem", fontSize: "0.75rem", display: "inline-block", width: "32px", textAlign: "right" }}>{Math.round(widthPct)}%</span></span>
              </div>
              <div style={{ width: "100%", height: "10px", background: "#f1f5f9", borderRadius: "5px", overflow: "hidden" }}>
                 <div style={{ width: `${Math.max(widthPct, 1)}%`, height: "100%", background: d.color, borderRadius: "5px", transition: "width 0.5s ease" }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};


export function ReportsHubPage() {
  const { applications, subcontractorMasters, engineerMasters, milestoneTemplates, storeYards, standardResources } = useAppState();
  const { store: geoStore } = useGeoDirectory();
  
  const [leads, setLeads] = useState<LeadTableRow[]>([]);
  const [poData, setPoData] = useState<IssuePoHeader[]>([]);
  const [issueData, setIssueData] = useState<IssueMaterialRow[]>([]);
  const [inwardData, setInwardData] = useState<StoreInwardReceipt[]>([]);
  const [outwardData, setOutwardData] = useState<StoreOutwardRow[]>([]);
  const [isLoadingBI, setIsLoadingBI] = useState(true);

  const [activeModule, setActiveModule] = useState<ModuleId>("leads");

  // Advanced BI Slicers
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [datePreset, setDatePreset] = useState("custom");
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear().toString());
  const [selectedMonth, setSelectedMonth] = useState(new Date().getMonth().toString());
  const [selectedQuarter, setSelectedQuarter] = useState(Math.floor(new Date().getMonth() / 3).toString());
  const [districtFilter, setDistrictFilter] = useState("");

  const applyPresetDates = (preset: string, y: number, m: number, q: number) => {
    const formatDate = (date: Date) => {
       const yy = date.getFullYear();
       const mm = String(date.getMonth() + 1).padStart(2, '0');
       const dd = String(date.getDate()).padStart(2, '0');
       return `${yy}-${mm}-${dd}`;
    };

    const now = new Date();
    let start = "";
    let end = "";
    
    if (preset === "today") {
      start = formatDate(now);
      end = start;
    } else if (preset === "weekly") {
      const d = new Date(now);
      d.setDate(d.getDate() - d.getDay());
      start = formatDate(d);
      const e = new Date(d);
      e.setDate(e.getDate() + 6);
      end = formatDate(e);
    } else if (preset === "monthly") {
      start = formatDate(new Date(y, m, 1));
      end = formatDate(new Date(y, m + 1, 0));
    } else if (preset === "quarterly") {
      start = formatDate(new Date(y, q * 3, 1));
      end = formatDate(new Date(y, q * 3 + 3, 0));
    } else if (preset === "yearly") {
      start = formatDate(new Date(y, 0, 1));
      end = formatDate(new Date(y, 11, 31));
    }

    if (preset !== "custom") {
      setDateFrom(start);
      setDateTo(end);
    }
  };

  const handleDatePreset = (preset: string) => {
    setDatePreset(preset);
    applyPresetDates(preset, parseInt(selectedYear), parseInt(selectedMonth), parseInt(selectedQuarter));
  };
  const handleYearChange = (val: string) => {
    setSelectedYear(val);
    applyPresetDates(datePreset, parseInt(val), parseInt(selectedMonth), parseInt(selectedQuarter));
  };
  const handleMonthChange = (val: string) => {
    setSelectedMonth(val);
    applyPresetDates(datePreset, parseInt(selectedYear), parseInt(val), parseInt(selectedQuarter));
  };
  const handleQuarterChange = (val: string) => {
    setSelectedQuarter(val);
    applyPresetDates(datePreset, parseInt(selectedYear), parseInt(selectedMonth), parseInt(val));
  };
  const [mandalFilter, setMandalFilter] = useState("");
  const [villageFilter, setVillageFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL"); 
  const [isFiltersOpen, setIsFiltersOpen] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const pageSize = 20;

  useEffect(() => {
    setCurrentPage(1);
  }, [activeModule, dateFrom, dateTo, districtFilter, mandalFilter, villageFilter, statusFilter]);

  useEffect(() => {
    Promise.all([
      fetchLeadsTable().catch(() => []),
      fetchIssuePoRegister().catch(() => []),
      fetchIssueMaterialRegister().catch(() => []),
      fetchStoreInwardRegister().catch(() => []),
      fetchStoreOutwardRegister().catch(() => [])
    ]).then(([leadsRes, poRes, issueRes, inRes, outRes]) => {
      setLeads(leadsRes as LeadTableRow[]);
      setPoData(poRes as IssuePoHeader[]);
      setIssueData(issueRes as IssueMaterialRow[]);
      setInwardData(inRes as StoreInwardReceipt[]);
      setOutwardData(outRes as StoreOutwardRow[]);
      setIsLoadingBI(false);
    });
  }, []);

  const passesDateFilter = (dStr?: string | null) => {
    if (!dateFrom && !dateTo) return true;
    if (!dStr) return false;
    const d = new Date(dStr);
    if (dateFrom && d < new Date(dateFrom)) return false;
    if (dateTo) {
      const to = new Date(dateTo);
      to.setHours(23, 59, 59, 999);
      if (d > to) return false;
    }
    return true;
  };

  const getSubReportFilters = (modId: ModuleId) => {
    switch (modId) {
      case "leads": return ["ALL", "approved", "rejected", "interested", "not_interested"];
      case "applicants": return ["ALL", "verif_pending", "ready_icici_mou", "mou_completed", "on_hold"];
      case "subcontractors": return ["ALL", "active", "inactive"];
      case "progress": return ["ALL", "delayed", "on_track"];
      case "po": return ["ALL", "full", "partial"];
      case "material_issue": return ["ALL", "pending", "completed"];
      default: return ["ALL"];
    }
  };

  const COLORS = ["#3b82f6", "#10b981", "#f59e0b", "#ef4444", "#8b5cf6", "#14b8a6", "#ec4899", "#f97316"];

  const currentReportData = useMemo(() => {
    let headers: string[] = [];
    let rows: (string | number)[][] = [];
    let kpis: { total: number, totalLabel?: string, totalColor?: string, metric1: { label: string, val: number, color?: string, badge?: string }, metric2: { label: string, val: number, color?: string, badge?: string }, metric3?: { label: string, val: number, color?: string, badge?: string }, metric4?: { label: string, val: number, color?: string, badge?: string } } = { total: 0, metric1: { label: "", val: 0 }, metric2: { label: "", val: 0 } };
    let donutData: { label: string, value: number, color: string }[] = [];
    let barData: { label: string, value: number }[] = [];
    let funnelData: { label: string, value: number, color: string }[] = [];
    let barTitle = "Top 5 Distribution Categories";
    let pieTitle = "Status Segmentation";

    const geoFilter = { district: districtFilter, mandal: mandalFilter, village: villageFilter };
    const hasGeoFilter = Boolean(districtFilter || mandalFilter || villageFilter);
    const passesGeo = (row: { district?: string | null; mandal?: string | null; village?: string | null }) =>
      geoLocationPassesFilter(geoStore, row, geoFilter);

    if (activeModule === "leads") {
      const getNormalizedStatus = (l: any) => {
        const s = (l.hoStatus || l.status || "Unknown").toLowerCase().replace(/\s+/g, "_");
        if (s.includes("approv")) return "approved";
        if (s.includes("reject")) return "rejected";
        if (s.includes("not_interested")) return "not_interested";
        if (s.includes("interested")) return "interested";
        return "pending_processing";
      };

      let f = leads.filter(l => passesDateFilter(l.createdAt) && passesGeo(l));
      if (statusFilter !== "ALL") f = f.filter(l => getNormalizedStatus(l) === statusFilter);
      headers = ["Lead ID", "Name", "Mobile", "District", "Mandal", "Village", "Status", "Date"];
      rows = f.map(l => [l.leadId, l.applicantName || "", l.mobileNumber || "", l.district || "", l.mandal || "", l.village || "", getNormalizedStatus(l).replace(/_/g, " ").toUpperCase(), l.createdAt?.split('T')[0] || ""]);
      const interested = f.filter(l => getNormalizedStatus(l) === "interested").length;
      const notInterested = f.filter(l => getNormalizedStatus(l) === "not_interested").length;
      const approved = f.filter(l => getNormalizedStatus(l) === "approved").length;
      const rejected = f.filter(l => getNormalizedStatus(l) === "rejected").length;
      const calcPct = (v: number) => f.length > 0 ? Math.round((v / f.length) * 100) + '%' : '0%';

      kpis = { 
        total: f.length, 
        totalLabel: "Total Leads",
        totalColor: "#0f172a",
        metric1: { label: "Interested", val: interested, color: "#f59e0b", badge: calcPct(interested) },
        metric2: { label: "Not Interested", val: notInterested, color: "#8b5cf6", badge: calcPct(notInterested) },
        metric3: { label: "Approved", val: approved, color: "#10b981", badge: calcPct(approved) },
        metric4: { label: "Rejected", val: rejected, color: "#ef4444", badge: calcPct(rejected) }
      };
      
      pieTitle = "Sanction Phase Distribution";
      const phaseDist = f.reduce((acc, l) => {
        const s = (l.hoStatus || l.status || "Unknown").toUpperCase();
        let bucket = "Other";
        if (s.includes("LIST 1")) bucket = "Phase 1 (List 1)";
        else if (s.includes("LIST 2")) bucket = "Phase 2 (List 2)";
        else if (s.includes("LIST 3")) bucket = "Phase 3 (List 3)";
        
        // Exclude empty/unknown if you only want to see actual lists, but we can include them
        acc[bucket] = (acc[bucket] || 0) + 1;
        return acc;
      }, {} as Record<string, number>);
      
      donutData = Object.entries(phaseDist)
        .sort((a, b) => b[1] - a[1]) // Sort by volume
        .map(([k, v], i) => ({ label: k, value: v, color: COLORS[i % COLORS.length] }));
      
      let groupKey = "district";
      barTitle = "Top 5 Districts";
      if (villageFilter) { groupKey = "status"; barTitle = "Top 5 Statuses"; }
      else if (mandalFilter) { groupKey = "village"; barTitle = "Top 5 Villages"; }
      else if (districtFilter) { groupKey = "mandal"; barTitle = "Top 5 Mandals"; }

      const dist = f.reduce((acc, l) => { 
        let val = groupKey === "status" ? getNormalizedStatus(l).replace(/_/g, " ").toUpperCase() : (l[groupKey as keyof typeof l] || "Unknown");
        acc[val as string] = (acc[val as string] || 0) + 1; 
        return acc; 
      }, {} as Record<string, number>);
      barData = Object.entries(dist).sort((a,b)=>b[1]-a[1]).slice(0, 5).map(([k,v]) => ({ label: k, value: v }));

      funnelData = [
        { label: "Total Target Leads", value: f.length, color: "#94a3b8" },
        { label: "Contacted / In Progress", value: f.filter(l => getNormalizedStatus(l) !== "pending_processing").length, color: "#3b82f6" },
        { label: "Interested (Converted)", value: f.filter(l => getNormalizedStatus(l) === "interested").length, color: "#f59e0b" },
        { label: "Qualified & Approved", value: f.filter(l => getNormalizedStatus(l) === "approved").length, color: "#10b981" }
      ];
    } 
    else if (activeModule === "applicants") {
      let f = applications.filter(a => passesDateFilter(a.receivedAt) && passesGeo(a));
      if (statusFilter !== "ALL") f = f.filter(a => a.status === statusFilter);
      headers = ["App ID", "Beneficiary", "Mobile", "District", "Mandal", "Status", "Subcontractor"];
      rows = f.map(a => [a.id, a.mobile.beneficiaryName, "N/A", a.district, a.mandal, a.status, subcontractorMasters.find(s => s.id === a.subcontractor?.subcontractorMasterId)?.firmName || ""]);
      const verifPending = f.filter(a => ["pending_validation", "verif_pending"].includes(a.status || "")).length;
      const readyIcici = f.filter(a => ["subcontractor_linked", "ready_icici_mou"].includes(a.status || "")).length;
      const mouCompleted = f.filter(a => ["work_initiated", "mou_completed"].includes(a.status || "")).length;
      const onHold = f.filter(a => a.status === "on_hold").length;
      const calcPct = (v: number) => f.length > 0 ? Math.round((v / f.length) * 100) + '%' : '0%';

      kpis = { 
        total: f.length, 
        totalLabel: "Total Applicants", 
        totalColor: "#0f172a",
        metric1: { label: "Verif. Pending", val: verifPending, color: "#f59e0b", badge: calcPct(verifPending) }, 
        metric2: { label: "Ready ICICI & MOU", val: readyIcici, color: "#0ea5e9", badge: calcPct(readyIcici) },
        metric3: { label: "MOU Completed", val: mouCompleted, color: "#10b981", badge: calcPct(mouCompleted) },
        metric4: { label: "On Hold", val: onHold, color: "#ef4444", badge: calcPct(onHold) }
      };
      
      const st = f.reduce((acc, a) => { acc[a.status || "Unknown"] = (acc[a.status || "Unknown"] || 0) + 1; return acc; }, {} as Record<string, number>);
      donutData = Object.entries(st).map(([k,v], i) => ({ label: k, value: v, color: COLORS[i % COLORS.length] }));
      
      const subs = f.reduce((acc, a) => { const s = subcontractorMasters.find(x => x.id === a.subcontractor?.subcontractorMasterId)?.firmName || "Unassigned"; acc[s] = (acc[s] || 0) + 1; return acc; }, {} as Record<string, number>);
      barData = Object.entries(subs).sort((a,b)=>b[1]-a[1]).slice(0, 5).map(([k,v]) => ({ label: k, value: v }));
    }
    else if (activeModule === "subcontractors") {
      let f = subcontractorMasters;
      if (hasGeoFilter) {
         const validSubIds = new Set(applications.filter(passesGeo).map(a => a.subcontractor?.subcontractorMasterId).filter(Boolean));
         f = f.filter(s => validSubIds.has(s.id));
      }
      if (statusFilter === "active") f = f.filter(s => applications.some(a => a.subcontractor?.subcontractorMasterId === s.id));
      if (statusFilter === "inactive") f = f.filter(s => !applications.some(a => a.subcontractor?.subcontractorMasterId === s.id));
      headers = ["ID", "Firm Name", "Contact", "Phone", "Assigned Apps"];
      rows = f.map(s => [s.id, s.firmName, s.contactPersonName || "", s.contactPhone || "", applications.filter(a => a.subcontractor?.subcontractorMasterId === s.id).length]);
      const activeFirms = f.filter(s => applications.some(a => a.subcontractor?.subcontractorMasterId === s.id)).length;
      const assignedApps = applications.filter(a => a.subcontractor?.subcontractorMasterId).length;
      const calcPct = (v: number) => f.length > 0 ? Math.round((v / f.length) * 100) + '%' : '0%';
      kpis = { 
        total: f.length, 
        totalLabel: "Total Firms", 
        totalColor: "#0f172a",
        metric1: { label: "Active Firms", val: activeFirms, color: "#10b981", badge: calcPct(activeFirms) }, 
        metric2: { label: "Total Assigned", val: assignedApps, color: "#3b82f6", badge: "" } 
      };
      
      const top = rows.sort((a,b) => (b[4] as number) - (a[4] as number)).slice(0, 5);
      barData = top.map(r => ({ label: String(r[1]), value: Number(r[4]) }));
    }
    else if (activeModule === "po") {
      let f = poData.filter(p => passesDateFilter(p.createdAt));
      // Try to filter by subcontractor assigned apps if po has subcontractorFirmName
      if (hasGeoFilter) {
         const validSubIds = new Set(applications.filter(passesGeo).map(a => a.subcontractor?.subcontractorMasterId).filter(Boolean));
         const validFirmNames = new Set(subcontractorMasters.filter(s => validSubIds.has(s.id)).map(s => s.firmName));
         f = f.filter(p => validFirmNames.has(p.subcontractorFirmName));
      }
      if (statusFilter !== "ALL") f = f.filter(p => p.selectionMode === statusFilter);
      headers = ["PO Number", "Milestone", "Subcontractor", "Mode", "Date"];
      rows = f.map(p => [p.poNumber, p.milestoneTitle, p.subcontractorFirmName, p.selectionMode, p.poDate || p.createdAt.split('T')[0]]);
      const full = f.filter(p => p.selectionMode === "full").length;
      const partial = f.filter(p => p.selectionMode === "partial").length;
      const calcPct = (v: number) => f.length > 0 ? Math.round((v / f.length) * 100) + '%' : '0%';
      kpis = { 
        total: f.length, 
        totalLabel: "Total POs",
        totalColor: "#0f172a",
        metric1: { label: "Full POs", val: full, color: "#10b981", badge: calcPct(full) }, 
        metric2: { label: "Partial POs", val: partial, color: "#f59e0b", badge: calcPct(partial) } 
      };
      
      const modes = f.reduce((acc, p) => { acc[p.selectionMode] = (acc[p.selectionMode] || 0) + 1; return acc; }, {} as Record<string, number>);
      donutData = Object.entries(modes).map(([k,v], i) => ({ label: k, value: v, color: COLORS[i % COLORS.length] }));
      
      const bySub = f.reduce((acc, p) => { acc[p.subcontractorFirmName] = (acc[p.subcontractorFirmName] || 0) + 1; return acc; }, {} as Record<string, number>);
      barData = Object.entries(bySub).sort((a,b)=>b[1]-a[1]).slice(0, 5).map(([k,v]) => ({ label: k, value: v }));
    }
    else if (activeModule === "material_issue") {
      let f = issueData.filter(i => passesDateFilter(i.createdAt));
      if (hasGeoFilter) {
         const validAppIds = new Set(applications.filter(passesGeo).map(a => a.id));
         f = f.filter(i => validAppIds.has(i.applicationId));
      }
      if (statusFilter !== "ALL") f = f.filter(i => i.status === statusFilter);
      headers = ["Issue ID", "App ID", "Resource", "Planned", "Issued", "Status"];
      rows = f.map(i => [i.id, i.applicationId, i.resourceItemLabel, i.plannedQty, i.issuedQty, i.status]);
      const completed = f.filter(i => i.status === "completed").length;
      const pending = f.filter(i => i.status !== "completed").length;
      const calcPct = (v: number) => f.length > 0 ? Math.round((v / f.length) * 100) + '%' : '0%';
      kpis = { 
        total: f.length, 
        totalLabel: "Total Issued",
        totalColor: "#0f172a",
        metric1: { label: "Completed", val: completed, color: "#10b981", badge: calcPct(completed) }, 
        metric2: { label: "Pending", val: pending, color: "#f59e0b", badge: calcPct(pending) } 
      };
      
      const stats = f.reduce((acc, i) => { acc[i.status] = (acc[i.status] || 0) + 1; return acc; }, {} as Record<string, number>);
      donutData = Object.entries(stats).map(([k,v], i) => ({ label: k, value: v, color: COLORS[i % COLORS.length] }));
      
      const res = f.reduce((acc, i) => { acc[i.resourceItemLabel] = (acc[i.resourceItemLabel] || 0) + i.plannedQty; return acc; }, {} as Record<string, number>);
      barData = Object.entries(res).sort((a,b)=>b[1]-a[1]).slice(0, 5).map(([k,v]) => ({ label: k, value: v }));
    }
    else {
      headers = ["Record ID", "Module", "Count"];
      rows = [["-", activeModule, 1]];
    }

    return { headers, rows, kpis, donutData, barData, barTitle, funnelData, pieTitle };
  }, [
    activeModule, leads, applications, subcontractorMasters, 
    poData, issueData, dateFrom, dateTo, districtFilter, mandalFilter, villageFilter, statusFilter, geoStore
  ]);

  const handleDownloadCsv = () => {
    downloadCsv(`BI_${activeModule}_${new Date().getTime()}.csv`, currentReportData.headers, currentReportData.rows.map(r => r.map(String)));
  };

  const handleDownloadPdf = async () => {
    const html = `
      <html>
        <head>
          <style>
            body { font-family: sans-serif; color: #111; }
            h1 { font-size: 20px; border-bottom: 2px solid #000; padding-bottom: 10px; text-transform: uppercase; margin-top: 0; }
            table { width: 100%; border-collapse: collapse; margin-top: 15px; font-size: 11px; }
            th, td { border: 1px solid #cbd5e1; padding: 6px; text-align: left; }
            th { background-color: #f1f5f9; color: #334155; }
          </style>
        </head>
        <body>
          <h1>Corporate BI Report: ${activeModule.toUpperCase()}</h1>
          <p style="font-size:12px; color:#64748b; margin-top:0;">Generated on: ${new Date().toLocaleString()} | Filter Segment: ${statusFilter}</p>
          <table>
            <thead><tr>${currentReportData.headers.map(h => `<th>${h}</th>`).join('')}</tr></thead>
            <tbody>${currentReportData.rows.map(r => `<tr>${r.map(c => `<td>${c}</td>`).join('')}</tr>`).join('')}</tbody>
          </table>
        </body>
      </html>
    `;
    await downloadPdfFromHtml(html, `BI_${activeModule}.pdf`);
  };

  if (isLoadingBI) return <div style={{ background: "#f8fafc", minHeight: "100vh", display: "flex", justifyContent: "center", alignItems: "center", color: "#64748b" }}><h2>Loading Data Model...</h2></div>;

  const totalPages = Math.max(1, Math.ceil(currentReportData.rows.length / pageSize));
  const paginatedRows = currentReportData.rows.slice((currentPage - 1) * pageSize, currentPage * pageSize);

  return (
    <div style={{ background: "#f8fafc", minHeight: "calc(100vh - 60px)", fontFamily: "'Inter', system-ui, sans-serif", padding: "1.25rem", boxSizing: "border-box", display: "flex", flexDirection: "column", position: "relative" }}>
      
      {/* Top Action Bar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1.25rem", flexShrink: 0 }}>
        <div>
          <h1 style={{ margin: 0, fontSize: "1.35rem", fontWeight: 700, color: "#111827", letterSpacing: "-0.01em" }}>{MODULES.find(m => m.id === activeModule)?.title} Analytics</h1>
          <p style={{ margin: "0.25rem 0 0", fontSize: "0.85rem", color: "#6b7280" }}>Last refreshed: {new Date().toLocaleTimeString()}</p>
        </div>
        <div style={{ display: "flex", gap: "0.75rem" }}>
          <button 
            onClick={() => setIsFiltersOpen(!isFiltersOpen)} 
            style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 1rem", borderRadius: "6px", background: isFiltersOpen ? "#f3f4f6" : "#fff", border: "1px solid #d1d5db", color: "#374151", fontWeight: 600, fontSize: "0.875rem", cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}
          >
            <IconFilter width={18} height={18} color="#4b5563" strokeWidth={isFiltersOpen ? 2 : 1.5} /> 
            Global Filters <span style={{ fontSize: "0.7rem", color: "#9ca3af" }}>{isFiltersOpen ? "▲" : "▼"}</span>
          </button>
          <button onClick={handleDownloadPdf} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 1rem", borderRadius: "6px", background: "#fff", border: "1px solid #d1d5db", color: "#374151", fontWeight: 600, fontSize: "0.875rem", cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
            <IconPdf width={18} height={18} color="#4b5563" /> Export PDF
          </button>
          <button onClick={handleDownloadCsv} style={{ display: "flex", alignItems: "center", gap: "0.5rem", padding: "0.5rem 1rem", borderRadius: "6px", background: "#fff", border: "1px solid #d1d5db", color: "#374151", fontWeight: 600, fontSize: "0.875rem", cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.05)" }}>
            <IconDownload width={18} height={18} color="#4b5563" /> Export Data
          </button>
        </div>
      </div>

      <div className="alert alert--info" role="note" style={{ marginBottom: "1.25rem", flexShrink: 0 }}>
        <strong>Note:</strong> Reports in this hub are under active development. Figures, charts, and exports may be incomplete or contain errors. If any information looks wrong, please verify it against the source registers before official use or decision-making. Upcoming updates will bring more accurate, real-time analytics and richer reporting across all modules.
      </div>

      {/* Global Slicers (Redesigned) */}
      {isFiltersOpen && (
        <div style={{ background: "#fff", padding: "1.25rem", borderRadius: "8px", boxShadow: modernShadow, border: "1px solid #f1f5f9", marginBottom: "1.25rem", flexShrink: 0, position: "relative", animation: "fadeIn 0.2s ease-in-out" }}>
          
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: "1rem" }}>
            <h3 style={{ margin: 0, fontSize: "0.95rem", fontWeight: 600, color: "#374151" }}>Filter Data</h3>
            <button 
              onClick={() => { 
                setDateFrom(""); setDateTo(""); setDatePreset("custom"); setDistrictFilter(""); setMandalFilter(""); setVillageFilter(""); setStatusFilter("ALL"); 
              }} 
              style={{ background: "#eff6ff", border: "none", color: "#3b82f6", fontSize: "0.8rem", fontWeight: 600, cursor: "pointer", padding: "0.4rem 0.8rem", borderRadius: "6px", transition: "all 0.2s" }}
            >
              Clear all slicers
            </button>
          </div>

          <div style={{ display: "flex", flexWrap: "wrap", gap: "1.5rem" }}>
            {/* Timeframe Filters */}
          <style>{`
            .react-datepicker-wrapper { width: 100%; }
            .custom-datepicker { border: 1px solid #cbd5e1; border-radius: 6px; padding: 0.4rem; font-size: 0.8rem; color: #334155; outline: none; width: 100%; box-sizing: border-box; }
            .custom-datepicker:focus { border-color: #3b82f6; box-shadow: 0 0 0 1px #3b82f6; }
          `}</style>
          <div style={{ display: "flex", gap: "1rem", flex: "1 1 auto", flexWrap: "wrap" }}>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: "100px", flex: 1 }}>
              <label style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Date Range</label>
              <SearchableSelect
                id="date-preset"
                value={datePreset}
                onChange={handleDatePreset}
                options={[
                  { value: "custom", label: "Custom" },
                  { value: "today", label: "Today" },
                  { value: "weekly", label: "This Week" },
                  { value: "monthly", label: "Monthly" },
                  { value: "quarterly", label: "Quarterly" },
                  { value: "yearly", label: "Yearly" }
                ]}
              />
            </div>
            
            {(datePreset === "monthly" || datePreset === "quarterly" || datePreset === "yearly") && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: "80px", flex: 1 }}>
                <label style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Year</label>
                <SearchableSelect
                  id="year-preset"
                  value={selectedYear}
                  onChange={handleYearChange}
                  options={[2024, 2025, 2026, 2027, 2028].map(y => ({ value: y.toString(), label: y.toString() }))}
                />
              </div>
            )}

            {datePreset === "monthly" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: "100px", flex: 1 }}>
                <label style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Month</label>
                <SearchableSelect
                  id="month-preset"
                  value={selectedMonth}
                  onChange={handleMonthChange}
                  options={["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((m, i) => ({ value: i.toString(), label: m }))}
                />
              </div>
            )}

            {datePreset === "quarterly" && (
              <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: "100px", flex: 1 }}>
                <label style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>Quarter</label>
                <SearchableSelect
                  id="quarter-preset"
                  value={selectedQuarter}
                  onChange={handleQuarterChange}
                  options={["Q1 (Jan-Mar)", "Q2 (Apr-Jun)", "Q3 (Jul-Sep)", "Q4 (Oct-Dec)"].map((q, i) => ({ value: i.toString(), label: q }))}
                />
              </div>
            )}

            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: "120px", flex: 1 }}>
              <label style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>From Date</label>
              <DatePicker
                selected={dateFrom ? new Date(dateFrom) : null}
                onChange={(d: Date | null) => { 
                   if (d) {
                     const yy = d.getFullYear();
                     const mm = String(d.getMonth() + 1).padStart(2, '0');
                     const dd = String(d.getDate()).padStart(2, '0');
                     setDateFrom(`${yy}-${mm}-${dd}`);
                   } else {
                     setDateFrom("");
                   }
                   setDatePreset("custom"); 
                }}
                dateFormat="dd-MM-yyyy"
                className="custom-datepicker"
                placeholderText="dd-mm-yyyy"
              />
            </div>
            <div style={{ display: "flex", flexDirection: "column", gap: "0.4rem", minWidth: "120px", flex: 1 }}>
              <label style={{ fontSize: "0.7rem", color: "#64748b", fontWeight: 700, textTransform: "uppercase" }}>To Date</label>
              <DatePicker
                selected={dateTo ? new Date(dateTo) : null}
                onChange={(d: Date | null) => { 
                   if (d) {
                     const yy = d.getFullYear();
                     const mm = String(d.getMonth() + 1).padStart(2, '0');
                     const dd = String(d.getDate()).padStart(2, '0');
                     setDateTo(`${yy}-${mm}-${dd}`);
                   } else {
                     setDateTo("");
                   }
                   setDatePreset("custom"); 
                }}
                dateFormat="dd-MM-yyyy"
                className="custom-datepicker"
                placeholderText="dd-mm-yyyy"
              />
            </div>
          </div>

          <div style={{ width: "1px", background: "#e2e8f0", display: "none" }} className="divider"></div>
          <style>{`@media (min-width: 1024px) { .divider { display: block !important; } }`}</style>

          {/* Geo Filters */}
          <div style={{ display: "flex", gap: "1rem", flex: "1.5 1 auto", flexWrap: "wrap" }}>
            <GeoLocationFilters
              idPrefix="reports-hub"
              district={districtFilter}
              mandal={mandalFilter}
              village={villageFilter}
              onDistrictChange={(d) => {
                setDistrictFilter(d);
                setMandalFilter("");
                setVillageFilter("");
              }}
              onMandalChange={(m) => {
                setMandalFilter(m);
                setVillageFilter("");
              }}
              onVillageChange={setVillageFilter}
            />
          </div>
        </div>
      </div>
      )}

      {/* Main Module Tabs (Top Nav) */}
      <div className="hide-scroll" style={{ display: "flex", gap: "1.5rem", overflowX: "auto", borderBottom: "1px solid #e2e8f0", marginBottom: "1.25rem", flexShrink: 0 }}>
        <style>{`.hide-scroll::-webkit-scrollbar { display: none; } .hide-scroll { -ms-overflow-style: none; scrollbar-width: none; }`}</style>
        {MODULES.map(m => (
          <button 
            key={m.id} onClick={() => { setActiveModule(m.id); setStatusFilter("ALL"); }}
            style={{ 
              background: "transparent",
              border: "none",
              borderBottom: activeModule === m.id ? "3px solid #3b82f6" : "3px solid transparent",
              padding: "0 0.25rem 0.75rem",
              color: activeModule === m.id ? "#1e293b" : "#64748b",
              fontWeight: activeModule === m.id ? 600 : 500,
              fontSize: "0.9rem",
              cursor: "pointer",
              whiteSpace: "nowrap",
              transition: "all 0.2s"
            }}
          >
            {m.title}
          </button>
        ))}
      </div>

      {/* Main Canvas Area */}
      <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem", flex: 1, minHeight: 0, paddingBottom: "1rem" }}>
        




        {/* KPI Row & Charts */}
        <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem", flexShrink: 0 }}>
          <div className="report-kpi-grid">
            <KpiWidget
              title={currentReportData.kpis.totalLabel || "Total Records"}
              value={currentReportData.kpis.total}
              color={currentReportData.kpis.totalColor || "#0f172a"}
              variant="hero"
            />
            {currentReportData.kpis.metric1?.label ? (
              <KpiWidget
                title={currentReportData.kpis.metric1.label}
                value={currentReportData.kpis.metric1.val}
                color={currentReportData.kpis.metric1.color || "#10b981"}
                badge={currentReportData.kpis.metric1.badge}
              />
            ) : null}
            {currentReportData.kpis.metric2?.label ? (
              <KpiWidget
                title={currentReportData.kpis.metric2.label}
                value={currentReportData.kpis.metric2.val}
                color={currentReportData.kpis.metric2.color || "#ef4444"}
                badge={currentReportData.kpis.metric2.badge}
              />
            ) : null}
            {currentReportData.kpis.metric3?.label ? (
              <KpiWidget
                title={currentReportData.kpis.metric3.label}
                value={currentReportData.kpis.metric3.val}
                color={currentReportData.kpis.metric3.color || "#f59e0b"}
                badge={currentReportData.kpis.metric3.badge}
              />
            ) : null}
            {currentReportData.kpis.metric4?.label ? (
              <KpiWidget
                title={currentReportData.kpis.metric4.label}
                value={currentReportData.kpis.metric4.val}
                color={currentReportData.kpis.metric4.color || "#8b5cf6"}
                badge={currentReportData.kpis.metric4.badge}
              />
            ) : null}
          </div>
          <div style={{ display: "flex", gap: "1.25rem", height: "250px" }}>
            <div style={{ flex: 1.1 }}><PieChart title={currentReportData.pieTitle} data={currentReportData.donutData} /></div>
            {currentReportData.funnelData && currentReportData.funnelData.length > 0 && (
              <div style={{ flex: 1 }}><FunnelChart title="Lead Conversion Pipeline" data={currentReportData.funnelData} /></div>
            )}
            <div style={{ flex: 1.3 }}><HorizontalBarChart title={currentReportData.barTitle} data={currentReportData.barData} /></div>
          </div>
        </div>

        {/* Matrix Visual */}
        <div style={{ background: "#fff", borderRadius: "8px", display: "flex", flexDirection: "column", flex: 1, minHeight: "350px", boxShadow: modernShadow, border: "1px solid #f1f5f9" }}>
          <div style={{ padding: "1rem 1.25rem", borderBottom: "1px solid #e2e8f0" }}>
            <h3 style={{ margin: 0, fontSize: "0.85rem", fontWeight: 600, color: "#334155" }}>Matrix Details</h3>
          </div>
          <div style={{ overflow: "auto", flex: 1 }}>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: "0.8rem" }}>
              <thead style={{ position: "sticky", top: 0, background: "#f8fafc", zIndex: 10 }}>
                <tr>
                  {currentReportData.headers.map((h, i) => <th key={i} style={{ padding: "0.75rem 1.25rem", color: "#475569", fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.5px", fontSize: "0.75rem", borderBottom: "1px solid #e2e8f0", whiteSpace: "nowrap" }}>{h}</th>)}
                </tr>
              </thead>
              <tbody>
                {paginatedRows.length === 0 ? (
                  <tr><td colSpan={currentReportData.headers.length} style={{ padding: "3rem", textAlign: "center", color: "#94a3b8" }}>(Blank)</td></tr>
                ) : (
                  paginatedRows.map((row, rIdx) => (
                    <tr key={rIdx} style={{ borderBottom: "1px solid #f1f5f9", transition: "background 0.15s" }} onMouseOver={e => e.currentTarget.style.background="#f8fafc"} onMouseOut={e => e.currentTarget.style.background="transparent"}>
                      {row.map((cell, cIdx) => <td key={cIdx} style={{ padding: "0.75rem 1.25rem", color: "#475569", whiteSpace: "nowrap" }}>{cell}</td>)}
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
          {/* Pagination Controls */}
          {currentReportData.rows.length > 0 && (
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.75rem 1.25rem", borderTop: "1px solid #e2e8f0", background: "#f8fafc", borderRadius: "0 0 8px 8px" }}>
              <span style={{ fontSize: "0.8rem", color: "#64748b" }}>
                Showing {(currentPage - 1) * pageSize + 1} to {Math.min(currentPage * pageSize, currentReportData.rows.length)} of {currentReportData.rows.length} entries
              </span>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button 
                  disabled={currentPage === 1} 
                  onClick={() => setCurrentPage(p => p - 1)} 
                  style={{ padding: "0.35rem 0.75rem", border: "1px solid #cbd5e1", background: currentPage === 1 ? "#f1f5f9" : "#fff", color: currentPage === 1 ? "#94a3b8" : "#334155", borderRadius: "6px", cursor: currentPage === 1 ? "not-allowed" : "pointer", fontSize: "0.8rem", fontWeight: 500 }}
                >
                  Previous
                </button>
                <button 
                  disabled={currentPage === totalPages} 
                  onClick={() => setCurrentPage(p => p + 1)} 
                  style={{ padding: "0.35rem 0.75rem", border: "1px solid #cbd5e1", background: currentPage === totalPages ? "#f1f5f9" : "#fff", color: currentPage === totalPages ? "#94a3b8" : "#334155", borderRadius: "6px", cursor: currentPage === totalPages ? "not-allowed" : "pointer", fontSize: "0.8rem", fontWeight: 500 }}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
